import { createSlice } from "@reduxjs/toolkit";
import {
  fetchClaimList,
  fetchUnclaimList,
  fetchLuckyDrawDetails,
  updateWinnerClaimFulfillmentStatus,
  updateAdminNotes,
  fetchAllWinnersForMetrics,
  fetchClaimSummary,
} from "./viewClaimList.action";

const initialState = {
  // Lucky draw details
  selectedLuckyDraw: null,

  // Tab state
  activeTab: 0, // 0 = TO PROCESS, 1 = UNCLAIMED PRIZE

  // Filters
  filters: {
    searchText: "",
    searchType: "prizeName",
    dateStart: null,
    dateEnd: null,
    statuses: "",
    prizeType: "",
  },
  appliedFilters: {
    searchText: "",
    searchType: "prizeName",
    dateStart: null,
    dateEnd: null,
    statuses: "",
    prizeType: "",
  },

  // Data
  winners: [],
  totalCount: 0,

  // Metrics
  metrics: {
    claimed: 0,
    unclaimed: 0,
    unprocessed: 0,
    processed: 0,
    rejected: 0,
  },

  // Selection
  selectedRows: [],

  // Pagination
  page: 0,
  rowsPerPage: 10,

  // Loading states
  isFetching: false,
  isExporting: false,
  isUpdating: false,

  // Dialogs
  adminDialog: {
    open: false,
    winner: null,
  },
  bulkProcessDialog: {
    open: false,
    winners: [],
  },

  // Errors
  error: null,
};

const viewClaimListSlice = createSlice({
  name: "viewClaimList",
  initialState,
  reducers: {
    // Tab
    setActiveTab(state, action) {
      state.activeTab = action.payload;
      state.selectedRows = [];
      state.page = 0;
    },

    // Filters
    setFilter(state, action) {
      const { field, value } = action.payload;
      state.filters[field] = value;
    },
    clearFilters(state) {
      state.filters = initialState.filters;
      state.appliedFilters = initialState.appliedFilters;
      state.page = 0;
    },
    applyFilters(state) {
      state.appliedFilters = { ...state.filters };
      state.page = 0;
    },

    // Selection
    selectRow(state, action) {
      const uuid = action.payload;
      if (state.selectedRows.includes(uuid)) {
        state.selectedRows = state.selectedRows.filter((id) => id !== uuid);
      } else {
        state.selectedRows.push(uuid);
      }
    },
    selectAllRows(state, action) {
      const { checked, uuids } = action.payload;
      if (checked) {
        state.selectedRows = uuids;
      } else {
        state.selectedRows = [];
      }
    },
    clearSelectedRows(state) {
      state.selectedRows = [];
    },

    // Pagination
    setPage(state, action) {
      state.page = action.payload;
    },
    setRowsPerPage(state, action) {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },

    // Admin Dialog
    openAdminDialog(state, action) {
      state.adminDialog.open = true;
      state.adminDialog.winner = action.payload;
    },
    closeAdminDialog(state) {
      state.adminDialog.open = false;
      state.adminDialog.winner = null;
    },

    // Bulk Process Dialog
    openBulkProcessDialog(state, action) {
      state.bulkProcessDialog.open = true;
      state.bulkProcessDialog.winners = action.payload;
    },
    closeBulkProcessDialog(state) {
      state.bulkProcessDialog.open = false;
      state.bulkProcessDialog.winners = [];
    },

    // Exporting state
    setIsExporting(state, action) {
      state.isExporting = action.payload;
    },

    // Reset state
    resetState(state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Fetch Claim List
    builder.addCase(fetchClaimList.pending, (state) => {
      state.isFetching = true;
      state.error = null;
    });
    builder.addCase(fetchClaimList.fulfilled, (state, action) => {
      state.isFetching = false;
      const claims = action.payload?.claims || [];
      state.winners = transformWinners(claims);
      state.totalCount = action.payload?.total || claims.length;
    });
    builder.addCase(fetchClaimList.rejected, (state, action) => {
      state.isFetching = false;
      state.error = action.payload;
      state.winners = [];
      state.totalCount = 0;
    });

    // Fetch Unclaim List
    builder.addCase(fetchUnclaimList.pending, (state) => {
      state.isFetching = true;
      state.error = null;
    });
    builder.addCase(fetchUnclaimList.fulfilled, (state, action) => {
      state.isFetching = false;
      const claims = action.payload?.claims || [];
      state.winners = transformWinners(claims);
      state.totalCount = action.payload?.total || claims.length;
    });
    builder.addCase(fetchUnclaimList.rejected, (state, action) => {
      state.isFetching = false;
      state.error = action.payload;
      state.winners = [];
      state.totalCount = 0;
    });

    // Fetch Lucky Draw Details
    builder.addCase(fetchLuckyDrawDetails.fulfilled, (state, action) => {
      state.selectedLuckyDraw = action.payload;
    });

    // Update Winner Claim Fulfillment Status
    builder.addCase(updateWinnerClaimFulfillmentStatus.pending, (state) => {
      state.isUpdating = true;
    });
    builder.addCase(updateWinnerClaimFulfillmentStatus.fulfilled, (state) => {
      state.isUpdating = false;
      state.selectedRows = [];
    });
    builder.addCase(updateWinnerClaimFulfillmentStatus.rejected, (state) => {
      state.isUpdating = false;
    });

    // Update Admin Notes
    builder.addCase(updateAdminNotes.pending, (state) => {
      state.isUpdating = true;
    });
    builder.addCase(updateAdminNotes.fulfilled, (state) => {
      state.isUpdating = false;
    });
    builder.addCase(updateAdminNotes.rejected, (state) => {
      state.isUpdating = false;
    });

    // Fetch All Winners for Metrics (legacy)
    builder.addCase(fetchAllWinnersForMetrics.fulfilled, (state, action) => {
      const { claimedWinners, unclaimedWinners } = action.payload;

      const allWinners = [...claimedWinners, ...unclaimedWinners].map(
        (winner) => ({
          claim_fulfill_status:
            winner.consumer_claim_status === "processed"
              ? 1
              : winner.consumer_claim_status === "rejected"
              ? 2
              : 0,
          consumer_claim_status: winner.consumer_claim_status,
        })
      );

      state.metrics = {
        claimed: claimedWinners.length,
        unclaimed: unclaimedWinners.length,
        unprocessed: allWinners.filter(
          (w) => w.consumer_claim_status === "unprocessed"
        ).length,
        processed: allWinners.filter(
          (w) => w.consumer_claim_status === "processed"
        ).length,
        rejected: allWinners.filter(
          (w) => w.consumer_claim_status === "rejected"
        ).length,
      };
    });

    // Fetch Claim Summary (new dedicated API)
    builder.addCase(fetchClaimSummary.fulfilled, (state, action) => {
      state.metrics = {
        claimed: action.payload?.claimed || 0,
        unclaimed: action.payload?.unclaimed || 0,
        unprocessed: action.payload?.unprocessed || 0,
        processed: action.payload?.processed || 0,
        rejected: action.payload?.rejected || 0,
      };
    });
  },
});

// Helper function to transform API response to internal format
function transformWinners(winnersData) {
  return winnersData.map((winner) => {
    let formattedAddress = "-";
    if (winner.winner_address) {
      if (typeof winner.winner_address === "string") {
        // Check if address is just commas and spaces (empty)
        const cleanedAddress = winner.winner_address.replace(/[,\s]/g, "");
        formattedAddress =
          cleanedAddress.length > 0 ? winner.winner_address : "-";
      } else if (typeof winner.winner_address === "object") {
        const addr = winner.winner_address;
        const parts = [
          addr.addressline,
          addr.city,
          addr.state,
          addr.postal_code,
          addr.country,
        ].filter(Boolean);
        formattedAddress = parts.length > 0 ? parts.join(", ") : "-";
      }
    }

    let formattedContact = "-";
    if (winner.winner_contact) {
      if (typeof winner.winner_contact === "string") {
        formattedContact = winner.winner_contact;
      } else if (typeof winner.winner_contact === "object") {
        formattedContact =
          winner.winner_contact?.phone_number ||
          winner.winner_contact?.phone ||
          "-";
      }
    }

    // Map consumer_claim_status to claim_fulfill_status
    // 0 = unprocessed, 1 = processed, 2 = rejected
    let claimFulfillStatus = 0;
    if (winner.consumer_claim_status === "processed") {
      claimFulfillStatus = 1;
    } else if (winner.consumer_claim_status === "rejected") {
      claimFulfillStatus = 2;
    }

    return {
      uuid: winner.uuid,
      claim_fulfill_status: claimFulfillStatus,
      consumer_claim_status: winner.consumer_claim_status,
      admin_notes: winner.admin_notes || "",
      won_date: winner.won_date,
      claimed_date: winner.claimed_date || null,
      processed_at: winner.processed_at || null,
      prize_type: parseInt(winner.prize_type, 10),
      participant: {
        name: winner.winner_name || "-",
        email: winner.winner_email || "-",
        contact_no: formattedContact,
        address: formattedAddress,
      },
      form_values: [],
      prizeName: winner.prize_name,
      prizeId: winner.lucky_draw_prizes_id,
      prizeImage: winner.prize_image,
      days_unclaimed: winner.days_unclaimed || null,
      rawContact: formattedContact,
      rawEmail: winner.winner_email || "-",
      rawAddress: formattedAddress,
    };
  });
}

export const viewClaimListReducer = viewClaimListSlice.reducer;
export const {
  setActiveTab,
  setFilter,
  clearFilters,
  applyFilters,
  selectRow,
  selectAllRows,
  clearSelectedRows,
  setPage,
  setRowsPerPage,
  openAdminDialog,
  closeAdminDialog,
  openBulkProcessDialog,
  closeBulkProcessDialog,
  setIsExporting,
  resetState,
} = viewClaimListSlice.actions;
